import { Component, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UpdateModalService } from './shared/services/update-modal.service';
import { environment } from '../environments/environment';
import { LocalStorageService } from './shared/services/local-storage.service';
import { StoredCollectionNames } from './models/stored-collection-names';
import { DisplayNpsModalService } from './shared/services/display-nps-modal.service';
import { Subscription } from 'rxjs';
import { IpService } from './shared/services/ip.service';
import { ApiExStatisticsService } from './shared/services/api/ex-statistics/api-ex-statistics.service';

import MEPConfiguration from 'src/assets/mep-configuration/version.json';
import { AuthenticationService } from './shared/services/api/authentication/authentication.service';
import { UserSessionStore } from './shared/services/user-session.store';
import { ImpersonationService } from './shared/services/impersonation.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FEATURE_PREFIX, FEATURES } from '../assets/mep-configuration/features-config';
import { LoginHelperService } from './components/login/login-helper.service';
import { isPublicAuthPath } from './shared/helpers/auth-routes';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy {
  private subscriptions: Array<Subscription> = [];
  canDisplayMenu!: boolean;
  displayGuestMenu = false;
  impersonatedUserEmail: string | null = null;
  impersonationHandled = false;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private updateModalService: UpdateModalService,
    private modalService: NgbModal,
    private localStorageService: LocalStorageService,
    private displayNpsModalService: DisplayNpsModalService,
    private ipService: IpService,
    private apiExStatisticsService: ApiExStatisticsService,
    private authService: AuthenticationService,
    private impersonateService: ImpersonationService,
    private sessionStore: UserSessionStore
  ) {
    this.promptForPassword();
    this.translate.setDefaultLang('fr');

    this.subscriptions.push(
      this.impersonateService.impersonatedUserEmailObs.subscribe(email => {
        this.impersonatedUserEmail = email;
      })
    );

    window.addEventListener('storage', event => {
      if (event.key === 'api_token' || event.key === 'impersonated_email') {
        const email = localStorage.getItem('impersonated_email');
        this.impersonatedUserEmail = email;

        if (email && !this.impersonationHandled) {
          this.impersonationHandled = true;
          this.initializeUserContext();
        }
      }
    });

    this.subscriptions.push(
      this.impersonateService.impersonationFinishedObs.pipe(filter(finished => finished)).subscribe(() => {
        if (!this.impersonationHandled) {
          this.impersonationHandled = true;
          this.initializeUserContext();
        }
      })
    );

    this.subscriptions.push(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        let route = this.router.routerState.root;
        while (route.firstChild) {
          route = route.firstChild;
        }
        const data = route.snapshot.data;
        // Default values to true (if not defined in the route data)
        this.canDisplayMenu = data['displayNavBar'] ?? true;
        this.displayGuestMenu = data['guestDisplay'] ?? true;

        const featureToHighlight = FEATURES[MEPConfiguration.highlight_feature];
        const moduleCondition = featureToHighlight?.module ? data['module'] === featureToHighlight.module : true;
        if (
          MEPConfiguration.display_update_modal &&
          featureToHighlight &&
          localStorage.getItem(FEATURE_PREFIX + MEPConfiguration.highlight_feature) !== 'true' &&
          this.sessionStore.isAuthenticated() &&
          !isPublicAuthPath(this.router.url) &&
          moduleCondition
        ) {
          if (!this.modalService.hasOpenModals()) {
            const modalComponent = featureToHighlight.modalComponent;
            if (modalComponent)
              this.modalService
                .open(modalComponent, {
                  backdrop: 'static',
                  keyboard: false,
                  centered: true,
                  windowClass: 'modal-auto',
                })
                .result.then(() => {
                  localStorage.setItem(FEATURE_PREFIX + MEPConfiguration.highlight_feature, 'true');
                });
          }
        }
      })
    );

    this.subscriptions.push(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        if (!isPublicAuthPath(this.router.url)) {
          this.displayNpsModalService.displayNpsModalIfNeeded().then();
        }
      })
    );

    if (environment.production) {
      this.updateModalService.setUpdateDetectionTimer(5000);
    }

    this.localStorageService.sanitizeLocalStorage(StoredCollectionNames.STORED_CONTEXTS);
    this.localStorageService.sanitizeLocalStorage(StoredCollectionNames.STORED_MATCH_STEMS);

    this.ipService.resolveUserIp();

    if (!isPublicAuthPath(window.location.pathname) && (this.sessionStore.isAuthenticated() || this.impersonationHandled)) {
      // Handle session change
      if (!localStorage.getItem('impersonated_email') && !window.location.pathname.includes('login-as-user')) {
        this.authService
          .authenticateWithCookie()
          .catch(() => null)
          .then(() => {
            this.initializeUserContext();
          });
      } else {
        this.initializeUserContext();
      }
    }
  }
  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  promptForPassword(): void {
    if (!(environment as any)['promptAccessPassword']) return;
    const timestamp = Date.now();
    const enigma = (timestamp - (timestamp % (24 * 60 * 60 * 1000))) % 999999;
    if (localStorage.getItem('accessPassword') === enigma.toString()) return;
    const password = window.prompt('Enter Alpha Access Password');
    if (password !== enigma.toString()) {
      alert('Incorrect password. Access denied.');
      this.promptForPassword();
    } else localStorage.setItem('accessPassword', password);
  }

  urlIsAllowedToDisplayHelp() {
    return !this.router.url.includes('login') && !this.router.url.includes('guest') && !this.router.url.includes('tenders');
  }

  async logout() {
    await this.authService.logout();
    location.reload();
  }

  private initializeUserContext() {
    this.apiExStatisticsService.setExplainStatistics();
    this.apiExStatisticsService.setAccountInfos();

    if (!this.route.snapshot.data['guestDisplay']) {
      this.sessionStore.restoreFromLocalStorage();
    }
  }

  stopImpersonation() {
    this.impersonateService.clearImpersonation();
    window.close();
  }
}
